import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, AlertTriangle, Loader2, X, PackagePlus } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { formatCents, todayISO } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { computeEstimateTotals, computeLineTotal } from '@/lib/estimateTotals';
import { seedCustomerFields } from '@/lib/phone';
import { SearchCombobox } from '@/components/SearchCombobox';
import { resolveSqftQuantity, describeSqftQuantity, isAreaUnit } from '@billflow/shared';
import type {
  DiscountType,
  Customer,
  InventoryItem,
  Estimate,
  EstimateItem,
  ItemCategory,
  SqftMode,
} from '@billflow/shared';

// ─── Local types ───────────────────────────────────────────────────────────────

interface LineRow {
  key: string;
  item_id: number | null;
  description: string;
  qtyStr: string;
  priceStr: string;
  discount_type: 'none' | 'pct' | 'fixed';
  discountStr: string;
  sqft_per_box: number | null;
  sqftStr: string;
  note: string;
  // Sq.ft area pricing fields
  unit: string; // item's UOM ('Sq.ft.' | 'Pcs' | 'Bag' | '')
  entryMode: SqftMode; // 'box' | 'sqft' — how the qty input is interpreted
  sqft_per_piece: number | null;
}

interface SearchItem {
  id: number;
  name: string;
  code: string | null;
  category: string;
}

interface SearchResponse {
  data: { items: SearchItem[] };
}

interface CustomerSearchResponse {
  data: { customers: Customer[] };
}

interface InventoryItemResponse {
  data: { item: InventoryItem };
}

interface EstimateDetailResponse {
  data: {
    estimate: Estimate & { customer_name?: string };
    items: EstimateItem[];
    customer?: Customer;
    returns?: Array<{ id: number }>;
  };
}

interface CreditWarning {
  credit_limit: number;
  outstanding: number;
  new_total: number;
  overage: number;
}

interface CreateEstimateResponse {
  data: {
    estimate: { id: number; number: string; customer_name?: string };
    warning?: CreditWarning;
  };
}

interface CreateCustomerResponse {
  data: { customer: Customer };
}

interface CreateInventoryItemResponse {
  data: { item: InventoryItem };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

let rowCounter = 0;
function nextKey(): string {
  return `row-${++rowCounter}`;
}

function blankRow(): LineRow {
  return {
    key: nextKey(),
    item_id: null,
    description: '',
    qtyStr: '1',
    priceStr: '',
    discount_type: 'none',
    discountStr: '',
    sqft_per_box: null,
    sqftStr: '',
    note: '',
    unit: '',
    entryMode: 'box',
    sqft_per_piece: null,
  };
}

// ── effectiveQty ─────────────────────────────────────────────────────────────
// Single source of truth for the resolved quantity used in BOTH the live total
// and the submit payload — ensures they can never diverge.
function effectiveQty(row: LineRow): number {
  const raw = parseFloat(row.qtyStr) || 0;
  if (!isAreaUnit(row.unit)) return raw;
  // Sq.ft. item: resolve area
  return resolveSqftQuantity(raw, row.entryMode, row.sqft_per_box ?? 0, row.sqft_per_piece ?? 0);
}

function rowToLineInput(r: LineRow) {
  const qty = effectiveQty(r); // uses resolved area for Sq.ft items, raw count otherwise
  const unitPrice = Math.round((parseFloat(r.priceStr) || 0) * 100);
  const discountType: DiscountType | null = r.discount_type === 'none' ? null : r.discount_type;
  const discountValue =
    r.discount_type === 'pct'
      ? parseFloat(r.discountStr) || 0
      : Math.round((parseFloat(r.discountStr) || 0) * 100);
  return { qty, unit_price: unitPrice, discount_type: discountType, discount_value: discountValue };
}

// ─── Customer Create Modal (popup) ─────────────────────────────────────────────

interface CustomerCreateModalProps {
  initialQuery: string;
  onCreated: (customer: Customer) => void;
  onClose: () => void;
}

function CustomerCreateModal({ initialQuery, onCreated, onClose }: CustomerCreateModalProps) {
  // The combobox searches name/code/phone, so the typed query isn't necessarily a
  // name — a phone-looking query seeds Phone (leaving Name for the user to fill).
  const seed = seedCustomerFields(initialQuery);
  const [name, setName] = useState(seed.name);
  const [phone, setPhone] = useState(seed.phone);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<CreateCustomerResponse>('/api/customers', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
        }),
      });
      const created = res.data.customer;
      toast.success(`Customer "${created.name}" created`);
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">New Customer</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cust-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="cust-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Customer name"
              autoFocus={seed.phone !== ''}
              disabled={loading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-phone">Phone</Label>
            <Input
              id="cust-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 905-555-0100"
              autoFocus={seed.phone === ''}
              disabled={loading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-email">Email</Label>
            <Input
              id="cust-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. name@example.com"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Customer Combobox ─────────────────────────────────────────────────────────

interface CustomerComboboxProps {
  value: number | null;
  displayName: string;
  onChange: (id: number, name: string) => void;
  disabled?: boolean;
  /** Called after Tab/Enter selects a customer — used to focus the first line's item. */
  onConfirmAdvance?: () => void;
}

function CustomerCombobox({
  value,
  displayName,
  onChange,
  disabled,
  onConfirmAdvance,
}: CustomerComboboxProps) {
  // The combobox text mirrors the selected customer's name when one is chosen, and
  // becomes a free search query while the user types. `query` is the single source
  // of input text (no read-only "selected" mode → no clear-to-search bug).
  const [query, setQuery] = useState(displayName);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();

  // Keep the field text in sync when the parent sets the customer name externally
  // (e.g. edit-mode prefill, or "Create customer" selecting the new record).
  useEffect(() => {
    setQuery(displayName);
  }, [displayName]);

  // Once a customer is selected (value !== null) the text equals its name and we stop
  // searching, so a freshly-selected customer doesn't re-open a dropdown of itself.
  // Typing drops the selection (value → null), which re-enables searching.
  const shouldSearch = query.length >= 1 && value === null;

  const { data, isFetching } = useQuery({
    queryKey: ['customer-search', query],
    queryFn: () => {
      const params = new URLSearchParams({ q: query, status: 'active' });
      return apiFetch<CustomerSearchResponse>(`/api/customers?${params}`);
    },
    enabled: shouldSearch,
    staleTime: 10_000,
  });

  const customers = shouldSearch ? (data?.data.customers ?? []) : [];

  function handleSelect(c: Customer) {
    onChange(c.id, c.name);
    setQuery(c.name);
  }

  function handleQueryChange(q: string) {
    setQuery(q);
    // Typing drops any prior selection so a new one (or free text) can take its place;
    // clearing the field returns to an empty, ready search. (Re-search is never blocked.)
    if (value !== null) onChange(0, q);
  }

  function handleCustomerCreated(c: Customer) {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    queryClient.invalidateQueries({ queryKey: ['customer-search'] });
    handleSelect(c);
  }

  const showCreate = !isFetching && query.trim().length >= 1 && value === null;

  return (
    <>
      <SearchCombobox<Customer>
        value={value}
        query={query}
        onQueryChange={handleQueryChange}
        results={customers}
        getKey={(c) => c.id}
        renderResult={(c) => (
          <>
            <span className="font-medium">{c.name}</span>
            {c.code && <span className="text-muted-foreground font-mono">{c.code}</span>}
            {c.phone && <span className="text-muted-foreground ml-auto">{c.phone}</span>}
          </>
        )}
        onSelect={handleSelect}
        onConfirmAdvance={onConfirmAdvance}
        placeholder="Search customers…"
        disabled={disabled}
        loading={isFetching && shouldSearch}
        promptMessage="Type to search customers"
        emptyMessage={value === null ? 'No customers found' : undefined}
        footer={(q) =>
          showCreate ? (
            <div className="border-t">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCreateModal(true);
                }}
              >
                <Plus className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">Create customer</span>
                <span className="font-medium text-foreground truncate">
                  &ldquo;{q.trim()}&rdquo;
                </span>
              </button>
            </div>
          ) : null
        }
      />

      {showCreateModal &&
        createPortal(
          <CustomerCreateModal
            initialQuery={query.trim()}
            onCreated={(c) => {
              handleCustomerCreated(c);
              setShowCreateModal(false);
            }}
            onClose={() => setShowCreateModal(false)}
          />,
          document.body,
        )}
    </>
  );
}

// ─── Item Create Form (embedded in portaled panel) ────────────────────────────

interface ItemCreateFormProps {
  initialName: string;
  onCreated: (item: InventoryItem) => void;
  onCancel: () => void;
}

function ItemCreateForm({ initialName, onCreated, onCancel }: ItemCreateFormProps) {
  const [name, setName] = useState(initialName);
  const [category, setCategory] = useState<ItemCategory>('goods');
  const [sellingPriceStr, setSellingPriceStr] = useState('');
  const [purchasePriceStr, setPurchasePriceStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const selling_price = Math.round((parseFloat(sellingPriceStr) || 0) * 100);
    const purchase_price = Math.round((parseFloat(purchasePriceStr) || 0) * 100);
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch<CreateInventoryItemResponse>('/api/inventory', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), category, selling_price, purchase_price }),
      });
      const created = res.data.item;
      queryClient.invalidateQueries({ queryKey: ['item-search'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      onCreated(created);
      toast.success(`Item "${created.name}" created`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create item');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      onMouseDown={(e) => e.stopPropagation()}
      className="p-3 space-y-2.5 border-t bg-muted/30"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <PackagePlus className="h-3.5 w-3.5 text-primary" />
          New Item
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {error && (
        <p className="text-xs text-destructive bg-destructive/5 px-2 py-1 rounded">{error}</p>
      )}

      <div className="space-y-1">
        <Label className="text-xs">Name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-7 text-xs"
          placeholder="Item name"
          autoFocus
          disabled={submitting}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Category</Label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ItemCategory)}
          className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          disabled={submitting}
        >
          <option value="goods">Goods</option>
          <option value="service">Service</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Selling price ($)</Label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={sellingPriceStr}
              onChange={(e) => setSellingPriceStr(e.target.value)}
              className="h-7 text-xs pl-5 tabular-nums"
              placeholder="0.00"
              disabled={submitting}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Purchase price ($)</Label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={purchasePriceStr}
              onChange={(e) => setPurchasePriceStr(e.target.value)}
              className="h-7 text-xs pl-5 tabular-nums"
              placeholder="0.00"
              disabled={submitting}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-0.5">
        <Button type="submit" size="sm" disabled={submitting} className="flex-1 h-7 text-xs">
          {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Create'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
          className="h-7 text-xs px-2"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ─── Item Search Combobox (per row) ───────────────────────────────────────────

interface ItemComboboxProps {
  value: number | null;
  description: string;
  onSelect: (item: InventoryItem) => void;
  onDescriptionChange: (desc: string) => void;
  disabled?: boolean;
  /** Called with the input element on mount so parent can register it in the focus registry */
  registerInput?: (el: HTMLInputElement | null) => void;
  /** Called after a keyboard Tab-select so parent can focus the Qty input */
  onAfterKeyboardSelect?: () => void;
}

function ItemCombobox({
  value,
  description,
  onSelect,
  onDescriptionChange,
  disabled,
  registerInput,
  onAfterKeyboardSelect,
}: ItemComboboxProps) {
  // The line's `description` IS the combobox text. Typing edits it AND drops any prior
  // item_id (the parent's onDescriptionChange resets item_id to null), so a wrongly-
  // selected item can always be re-searched — this is what fixes the clear-to-search bug.
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();

  // Once an item is selected (value !== null) the text equals its name; we stop
  // searching so the dropdown doesn't re-open on the just-picked item. Typing resets
  // value to null (via onDescriptionChange in the parent), which re-enables searching.
  const shouldSearch = description.length >= 1 && value === null;

  const { data, isFetching } = useQuery({
    queryKey: ['item-search', description],
    queryFn: () => apiFetch<SearchResponse>(`/api/search?q=${encodeURIComponent(description)}`),
    enabled: shouldSearch,
    staleTime: 10_000,
  });

  const items = shouldSearch ? (data?.data.items ?? []) : [];

  async function handleSelect(si: SearchItem) {
    setShowCreateForm(false);
    try {
      const res = await apiFetch<InventoryItemResponse>(`/api/inventory/${si.id}`);
      onSelect(res.data.item);
    } catch {
      toast.error('Failed to load item details');
    }
  }

  function handleCreated(item: InventoryItem) {
    setShowCreateForm(false);
    onSelect(item);
  }

  const showCreate = !isFetching && description.trim().length >= 1 && value === null;

  return (
    <SearchCombobox<SearchItem>
      value={value}
      query={description}
      onQueryChange={onDescriptionChange}
      results={items}
      getKey={(si) => si.id}
      renderResult={(si) => (
        <>
          <span className="font-medium flex-1 truncate">{si.name}</span>
          {si.code && <span className="font-mono text-muted-foreground">{si.code}</span>}
        </>
      )}
      onSelect={(si) => handleSelect(si)}
      onConfirmAdvance={onAfterKeyboardSelect}
      registerInput={registerInput}
      allowFreeText
      placeholder="Search or type description…"
      className="h-8 text-xs"
      disabled={disabled}
      loading={isFetching && shouldSearch}
      promptMessage="Type to search inventory"
      emptyMessage={value === null && !showCreateForm ? 'No inventory items found' : undefined}
      footer={(q) =>
        value === null ? (
          <>
            {showCreate && !showCreateForm && (
              <div className="border-t">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-muted transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCreateForm(true);
                  }}
                >
                  <PackagePlus className="h-3 w-3 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">Create item</span>
                  <span className="font-medium text-foreground truncate">
                    &ldquo;{q.trim()}&rdquo;
                  </span>
                </button>
              </div>
            )}
            {showCreateForm && (
              <ItemCreateForm
                initialName={q.trim()}
                onCreated={(item) => {
                  queryClient.invalidateQueries({ queryKey: ['item-search'] });
                  handleCreated(item);
                }}
                onCancel={() => setShowCreateForm(false)}
              />
            )}
          </>
        ) : null
      }
    />
  );
}

// ─── Line Item Row ─────────────────────────────────────────────────────────────

interface LineRowProps {
  row: LineRow;
  index: number;
  onChange: (key: string, patch: Partial<LineRow>) => void;
  onRemove: (key: string) => void;
  lineTotal: number; // cents
  disabled?: boolean;
  isLast?: boolean;
  onAddRowAndFocus?: () => void;
  onFocusNextItemSearch?: () => void;
  registerItemInput?: (el: HTMLInputElement | null) => void;
}

function LineItemRow({
  row,
  index,
  onChange,
  onRemove,
  lineTotal,
  disabled,
  isLast,
  onAddRowAndFocus,
  onFocusNextItemSearch,
  registerItemInput,
}: LineRowProps) {
  const isSqft = isAreaUnit(row.unit);
  const qtyRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  function handleItemSelect(item: InventoryItem) {
    onChange(row.key, {
      item_id: item.id,
      description: item.name,
      priceStr: String(item.selling_price / 100),
      sqft_per_box: item.sqft_per_box,
      sqft_per_piece: item.sqft_per_piece ?? null,
      unit: item.unit ?? '',
      entryMode: 'box',
      // Legacy sqftStr kept for compat but effectively unused for area items now
      sqftStr: '',
    });
  }

  // A Sq.ft. item only shows the Box/ft² toggle + box/piece breakdown when a box
  // size is configured; without one the qty box IS the area in sq ft (typed directly).
  const hasBoxSize = (row.sqft_per_box ?? 0) > 0;

  // Build resolution caption for Sq.ft lines (only meaningful with a box size)
  const resolvedArea = isSqft ? effectiveQty(row) : null;
  const rawInput = parseFloat(row.qtyStr) || 0;

  let sqftCaption: string | null = null;
  if (isSqft && hasBoxSize && resolvedArea !== null && rawInput > 0) {
    const breakdown = describeSqftQuantity(resolvedArea, row.sqft_per_box ?? 0, row.sqft_per_piece);
    sqftCaption = `→ ${resolvedArea.toFixed(2)} sq ft (${breakdown})`;
  }

  // Qty label hint
  const qtyLabel = isSqft ? (hasBoxSize && row.entryMode === 'box' ? 'boxes' : 'sq ft') : null;

  return (
    <>
      <tr className="hover:bg-muted/20 transition-colors group">
        {/* # */}
        <td className="px-2 py-2 text-xs text-muted-foreground text-center w-7 select-none">
          {index + 1}
        </td>

        {/* Item / Description */}
        <td className="px-1 py-1.5 min-w-[150px]">
          <ItemCombobox
            value={row.item_id}
            description={row.description}
            onSelect={handleItemSelect}
            onDescriptionChange={(desc) => {
              // Typing in the field reverts the row to free-text: keep the text but
              // drop the linked item and its derived (price/sqft/unit) fields so the
              // dropdown can search again. Price is intentionally preserved so an
              // edited description on a priced line keeps its price.
              if (row.item_id !== null) {
                onChange(row.key, {
                  description: desc,
                  item_id: null,
                  sqft_per_box: null,
                  sqft_per_piece: null,
                  unit: '',
                  entryMode: 'box',
                });
              } else {
                onChange(row.key, { description: desc });
              }
            }}
            disabled={disabled}
            registerInput={registerItemInput}
            onAfterKeyboardSelect={() => qtyRef.current?.focus()}
          />
          {sqftCaption && (
            <p className="mt-0.5 text-[11px] font-mono text-muted-foreground pl-1 leading-tight">
              {sqftCaption}
            </p>
          )}
        </td>

        {/* Box | ft² entry-mode toggle (Sq.ft. items only) — between description and qty */}
        <td className="px-1 py-1.5 w-16 align-middle">
          {isSqft && hasBoxSize && (
            <div
              className="flex items-center rounded-md overflow-hidden border border-input h-5"
              tabIndex={-1}
            >
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => onChange(row.key, { entryMode: 'box' })}
                className={`flex-1 text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1 ${
                  row.entryMode === 'box'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                Box
              </button>
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => onChange(row.key, { entryMode: 'sqft' })}
                className={`flex-1 text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1 border-l border-input ${
                  row.entryMode === 'sqft'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                ft²
              </button>
            </div>
          )}
        </td>

        {/* Qty */}
        <td className="px-1 py-1.5 w-20">
          <div className="relative">
            <Input
              ref={qtyRef}
              type="number"
              min="0"
              step="any"
              value={row.qtyStr}
              onChange={(e) => onChange(row.key, { qtyStr: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  priceRef.current?.focus();
                }
              }}
              className="h-8 text-xs text-right tabular-nums"
              disabled={disabled}
            />
            {qtyLabel && (
              <span className="absolute right-1.5 top-full mt-0.5 text-[9px] text-muted-foreground pointer-events-none leading-none whitespace-nowrap">
                {qtyLabel}
              </span>
            )}
          </div>
        </td>

        {/* Unit Price */}
        <td className="px-1 py-1.5 w-28">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              $
            </span>
            <Input
              ref={priceRef}
              type="number"
              min="0"
              step="0.01"
              value={row.priceStr}
              onChange={(e) => onChange(row.key, { priceStr: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  if (isLast) {
                    onAddRowAndFocus?.();
                  } else {
                    onFocusNextItemSearch?.();
                  }
                }
              }}
              className="h-8 text-xs text-right pl-5 tabular-nums"
              disabled={disabled}
              placeholder="0.00"
            />
            {isSqft && (
              <span className="absolute right-2 top-full mt-0.5 text-[9px] text-muted-foreground pointer-events-none leading-none whitespace-nowrap">
                /sq.ft
              </span>
            )}
          </div>
        </td>

        {/* Discount */}
        <td className="px-1 py-1.5 w-40">
          <div className="flex items-center gap-1">
            <select
              value={row.discount_type}
              onChange={(e) =>
                onChange(row.key, {
                  discount_type: e.target.value as LineRow['discount_type'],
                  discountStr: '',
                })
              }
              className="h-8 rounded-md border border-input bg-background px-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              disabled={disabled}
            >
              <option value="none">None</option>
              <option value="pct">%</option>
              <option value="fixed">$</option>
            </select>
            {row.discount_type !== 'none' && (
              <div className="relative flex-1">
                {row.discount_type === 'fixed' && (
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                    $
                  </span>
                )}
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  max={row.discount_type === 'pct' ? '100' : undefined}
                  value={row.discountStr}
                  onChange={(e) => onChange(row.key, { discountStr: e.target.value })}
                  className={`h-8 text-xs text-right tabular-nums ${row.discount_type === 'fixed' ? 'pl-5' : ''}`}
                  disabled={disabled}
                  placeholder={row.discount_type === 'pct' ? '0' : '0.00'}
                />
              </div>
            )}
          </div>
        </td>

        {/* Line Total */}
        <td className="px-2 py-1.5 text-right text-xs tabular-nums font-medium w-28">
          {formatCents(lineTotal)}
        </td>

        {/* Remove */}
        <td className="px-1 py-1.5 w-8">
          <button
            type="button"
            onClick={() => onRemove(row.key)}
            disabled={disabled}
            className="opacity-0 group-hover:opacity-100 rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all disabled:pointer-events-none"
            title="Remove row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      <tr className="border-b last:border-0">
        <td />
        <td colSpan={7} className="px-1 pb-1.5 pt-0">
          <input
            type="text"
            value={row.note}
            onChange={(e) => onChange(row.key, { note: e.target.value })}
            disabled={disabled}
            placeholder="Optional remark (shows on the PDF)…"
            className="w-full rounded border-0 border-b border-dashed border-muted-foreground/30 bg-transparent px-1 py-0.5 text-xs italic text-muted-foreground placeholder:text-muted-foreground/40 focus:border-primary/50 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          />
        </td>
      </tr>
    </>
  );
}

// ─── Totals Panel ──────────────────────────────────────────────────────────────

interface TotalsPanelProps {
  subtotal: number;
  lineDiscountTotal: number;
  invoiceDiscountAmount: number;
  total: number;
  invoiceDiscountType: 'none' | 'pct' | 'fixed';
  invoiceDiscountStr: string;
  onDiscountTypeChange: (t: 'none' | 'pct' | 'fixed') => void;
  onDiscountStrChange: (s: string) => void;
  disabled?: boolean;
}

function TotalsPanel({
  subtotal,
  lineDiscountTotal,
  invoiceDiscountAmount,
  total,
  invoiceDiscountType,
  invoiceDiscountStr,
  onDiscountTypeChange,
  onDiscountStrChange,
  disabled,
}: TotalsPanelProps) {
  return (
    <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Totals
      </h3>

      {/* Invoice Discount */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Invoice Discount</Label>
        <div className="flex items-center gap-1.5">
          <select
            value={invoiceDiscountType}
            onChange={(e) => onDiscountTypeChange(e.target.value as 'none' | 'pct' | 'fixed')}
            className="h-8 rounded-md border border-input bg-background px-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            disabled={disabled}
          >
            <option value="none">None</option>
            <option value="pct">%</option>
            <option value="fixed">$</option>
          </select>
          {invoiceDiscountType !== 'none' && (
            <div className="relative flex-1">
              {invoiceDiscountType === 'fixed' && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  $
                </span>
              )}
              <Input
                type="number"
                min="0"
                step="0.01"
                max={invoiceDiscountType === 'pct' ? '100' : undefined}
                value={invoiceDiscountStr}
                onChange={(e) => onDiscountStrChange(e.target.value)}
                className={`h-8 text-xs text-right tabular-nums ${invoiceDiscountType === 'fixed' ? 'pl-5' : ''}`}
                disabled={disabled}
                placeholder={invoiceDiscountType === 'pct' ? '0' : '0.00'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Breakdown */}
      <div className="space-y-1.5 pt-1 border-t">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="tabular-nums font-mono text-xs">{formatCents(subtotal)}</span>
        </div>
        {lineDiscountTotal > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Line Discounts</span>
            <span className="tabular-nums font-mono text-xs text-amber-700">
              −{formatCents(lineDiscountTotal)}
            </span>
          </div>
        )}
        {invoiceDiscountAmount > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Invoice Discount</span>
            <span className="tabular-nums font-mono text-xs text-amber-700">
              −{formatCents(invoiceDiscountAmount)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="font-semibold text-sm">Total</span>
          <span className="tabular-nums font-semibold text-base font-mono">
            {formatCents(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Issue Panel ───────────────────────────────────────────────────────────────

interface IssuePanelProps {
  collectPayment: boolean;
  onCollectPaymentChange: (v: boolean) => void;
  paymentAmountStr: string;
  onPaymentAmountChange: (v: string) => void;
  paymentDate: string;
  onPaymentDateChange: (v: string) => void;
  pickedUpNow: boolean;
  onPickedUpNowChange: (v: boolean) => void;
  expectedPickupDate: string;
  onExpectedPickupDateChange: (v: string) => void;
  total: number;
  disabled?: boolean;
}

function IssuePanel({
  collectPayment,
  onCollectPaymentChange,
  paymentAmountStr,
  onPaymentAmountChange,
  paymentDate,
  onPaymentDateChange,
  pickedUpNow,
  onPickedUpNowChange,
  expectedPickupDate,
  onExpectedPickupDateChange,
  total,
  disabled,
}: IssuePanelProps) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <input
          id="collect-payment"
          type="checkbox"
          checked={collectPayment}
          onChange={(e) => {
            onCollectPaymentChange(e.target.checked);
            // Pre-fill the amount with the full total (balance due) when first enabled.
            if (e.target.checked && !paymentAmountStr) {
              onPaymentAmountChange((total / 100).toFixed(2));
            }
          }}
          className="rounded border-gray-300"
          disabled={disabled}
        />
        <Label htmlFor="collect-payment" className="text-sm cursor-pointer">
          Collect payment now?
        </Label>
      </div>
      {collectPayment && (
        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="payment-amount" className="text-xs">
              Amount ($)
            </Label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                $
              </span>
              <Input
                id="payment-amount"
                type="number"
                min="0"
                step="0.01"
                value={paymentAmountStr}
                onChange={(e) => onPaymentAmountChange(e.target.value)}
                className="pl-5 h-8 text-xs tabular-nums"
                placeholder={String((total / 100).toFixed(2))}
                disabled={disabled}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="payment-date" className="text-xs">
              Date
            </Label>
            <Input
              id="payment-date"
              type="date"
              value={paymentDate}
              onChange={(e) => onPaymentDateChange(e.target.value)}
              className="h-8 text-xs"
              disabled={disabled}
            />
          </div>
        </div>
      )}

      <div className="border-t border-blue-200 dark:border-blue-900 pt-3 space-y-2">
        <div className="flex items-center gap-2">
          <input
            id="picked-up-now"
            type="checkbox"
            checked={pickedUpNow}
            onChange={(e) => onPickedUpNowChange(e.target.checked)}
            className="rounded border-gray-300"
            disabled={disabled}
          />
          <Label htmlFor="picked-up-now" className="text-sm cursor-pointer">
            Customer picking up in full now
          </Label>
        </div>
        {!pickedUpNow && (
          <div className="space-y-1.5 pt-1">
            <Label htmlFor="expected-pickup-date" className="text-xs">
              Expected pickup date (optional)
            </Label>
            <Input
              id="expected-pickup-date"
              type="date"
              value={expectedPickupDate}
              onChange={(e) => onExpectedPickupDateChange(e.target.value)}
              className="h-8 text-xs w-44"
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Credit Warning Banner ─────────────────────────────────────────────────────

function CreditWarningBanner({
  warning,
  customerName,
}: {
  warning: CreditWarning;
  customerName: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3">
      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-amber-800 dark:text-amber-300">
        <span className="font-semibold">Heads up:</span> This brings{' '}
        <span className="font-semibold">{customerName}</span>&apos;s outstanding balance to{' '}
        <span className="font-semibold">{formatCents(warning.new_total)}</span>, exceeding their
        credit limit of <span className="font-semibold">{formatCents(warning.credit_limit)}</span>{' '}
        by <span className="font-semibold">{formatCents(warning.overage)}</span>. The estimate was
        still saved.
      </p>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function EstimateFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Form state ──
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  // New estimates start with 4 blank rows (most orders are 3–4 items); edit mode
  // starts with one placeholder that the prefill effect replaces with saved lines.
  const [rows, setRows] = useState<LineRow[]>(() =>
    isEdit ? [blankRow()] : Array.from({ length: 4 }, () => blankRow()),
  );
  const [invoiceDiscountType, setInvoiceDiscountType] = useState<'none' | 'pct' | 'fixed'>('none');
  const [invoiceDiscountStr, setInvoiceDiscountStr] = useState('');

  // ── Payment (collected only when issuing) ──
  const [collectPayment, setCollectPayment] = useState(false);
  const [paymentAmountStr, setPaymentAmountStr] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());
  // Pickup: most customers collect right away, so default ON; uncheck to schedule later.
  const [pickedUpNow, setPickedUpNow] = useState(true);
  const [expectedPickupDate, setExpectedPickupDate] = useState('');

  // ── Submit state ──
  const [submitting, setSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState<'draft' | 'issue' | null>(null);

  // ── Credit warning after submit ──
  const [creditWarning, setCreditWarning] = useState<CreditWarning | null>(null);

  const { user } = useAuthContext();

  // ── Load estimate for edit mode ──
  const { data: editData, isLoading: editLoading } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => apiFetch<EstimateDetailResponse>(`/api/estimates/${id}`),
    enabled: isEdit,
    staleTime: Infinity,
  });

  // Guard: the edit form is reachable directly by URL, so redirect out when this
  // estimate can't actually be edited (admin + draft/issued only — matches the server).
  useEffect(() => {
    if (!isEdit || !editData) return;
    const st = editData.data.estimate.status;
    const hasReturns = (editData.data.returns?.length ?? 0) > 0;
    // Operators may edit DRAFT estimates; ISSUED stays admin-only.
    const editable = (st === 'draft' || (user?.role === 'admin' && st === 'issued')) && !hasReturns;
    if (!editable) {
      toast.error(
        hasReturns
          ? 'This estimate has returns and can no longer be edited.'
          : 'This estimate can no longer be edited.',
      );
      navigate(`/estimates/${id}`, { replace: true });
    }
  }, [isEdit, editData, user, id, navigate]);

  // Pre-fill form when edit data arrives — run exactly ONCE so a refetch
  // (e.g. on window focus) never clobbers in-progress edits.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!editData || prefilled.current) return;
    prefilled.current = true;
    const est = editData.data.estimate;
    const items = editData.data.items;

    setCustomerId(est.customer_id);
    // Customer name comes from the joined `customer` object in the detail response,
    // not from the estimate row itself.
    setCustomerName(
      editData.data.customer?.name ??
        (est as Estimate & { customer_name?: string }).customer_name ??
        '',
    );
    setDate(est.date.slice(0, 10));
    setNotes(est.notes ?? '');
    setInvoiceDiscountType(
      est.invoice_discount_type === 'pct'
        ? 'pct'
        : est.invoice_discount_type === 'fixed'
          ? 'fixed'
          : 'none',
    );
    setInvoiceDiscountStr(
      est.invoice_discount_type === 'pct'
        ? String(est.invoice_discount_value)
        : est.invoice_discount_type === 'fixed'
          ? String(est.invoice_discount_value / 100)
          : '',
    );
    setRows(
      items.map((it) => {
        // Reconstruct unit from the line's item (joined from inventory_items) or default.
        const unit = it.unit ?? '';
        const isSqftLine = isAreaUnit(unit);
        const spb = it.sqft_per_box ?? 0;

        // Reconstruct entryMode and qtyStr from stored area (it.qty = area for sqft items)
        let entryMode: SqftMode = 'box';
        let qtyStr = String(it.qty);
        if (isSqftLine && spb > 0) {
          const area = it.qty;
          const isWholeBox = spb > 0 && Math.abs(area / spb - Math.round(area / spb)) < 1e-6;
          if (isWholeBox) {
            entryMode = 'box';
            qtyStr = String(Math.round(area / spb));
          } else {
            entryMode = 'sqft';
            qtyStr = String(area);
          }
        }

        return {
          key: nextKey(),
          item_id: it.item_id,
          description: it.description,
          qtyStr,
          priceStr: String(it.unit_price / 100),
          discount_type:
            it.discount_type === 'pct' ? 'pct' : it.discount_type === 'fixed' ? 'fixed' : 'none',
          discountStr:
            it.discount_type === 'pct'
              ? String(it.discount_value)
              : it.discount_type === 'fixed'
                ? String(it.discount_value / 100)
                : '',
          sqft_per_box: it.sqft_per_box,
          sqft_per_piece: it.sqft_per_piece ?? null,
          sqftStr: it.sqft != null ? String(it.sqft) : '',
          note: it.note ?? '',
          unit,
          entryMode,
        };
      }),
    );
  }, [editData]);

  // ── Row mutation helpers ──
  const handleRowChange = useCallback((key: string, patch: Partial<LineRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const handleRowRemove = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  // ── Cross-row focus registry ──
  // Maps row.key → the ItemCombobox search input element for that row.
  const itemInputRegistry = useRef<Map<string, HTMLInputElement | null>>(new Map());
  // Stable register callbacks — keyed by row.key so the registration useEffect
  // inside ItemCombobox doesn't re-fire on every parent render.
  const registerCallbackCache = useRef<Map<string, (el: HTMLInputElement | null) => void>>(
    new Map(),
  );
  const makeRegisterInput = useCallback((rowKey: string) => {
    if (!registerCallbackCache.current.has(rowKey)) {
      registerCallbackCache.current.set(rowKey, (el: HTMLInputElement | null) => {
        if (el) {
          itemInputRegistry.current.set(rowKey, el);
        } else {
          itemInputRegistry.current.delete(rowKey);
          // Also clean up the callback itself when the row unmounts.
          registerCallbackCache.current.delete(rowKey);
        }
      });
    }
    return registerCallbackCache.current.get(rowKey)!;
  }, []);
  // When addRow is called via keyboard (Price Tab on last row), we store the new
  // row's key here so a useEffect can focus it once it mounts and registers.
  const pendingFocusKey = useRef<string | null>(null);

  function addRow() {
    const newRow = blankRow();
    pendingFocusKey.current = newRow.key;
    setRows((prev) => [...prev, newRow]);
  }

  // After a keyboard-triggered addRow, focus the new row's item-search input
  // once it has registered itself.
  useEffect(() => {
    const key = pendingFocusKey.current;
    if (!key) return;
    const el = itemInputRegistry.current.get(key);
    if (el) {
      el.focus();
      pendingFocusKey.current = null;
    }
    // Run on every rows change so we catch the frame after the new row mounts.
  }, [rows]);

  // ── Live totals ──
  const lineInputs = rows.map(rowToLineInput);
  const invDiscType: DiscountType | null =
    invoiceDiscountType === 'none' ? null : invoiceDiscountType;
  const invDiscValue =
    invoiceDiscountType === 'pct'
      ? parseFloat(invoiceDiscountStr) || 0
      : Math.round((parseFloat(invoiceDiscountStr) || 0) * 100);

  const totals = computeEstimateTotals(lineInputs, invDiscType, invDiscValue);
  const lineTotals = rows.map((_r, i) => computeLineTotal(lineInputs[i]).line_total);

  // ── Validation ──
  function validate(): boolean {
    if (!customerId) {
      toast.error('Select a customer');
      return false;
    }
    const filledRows = rows.filter((r) => r.description.trim());
    if (filledRows.length === 0) {
      toast.error('Add at least one line item');
      return false;
    }
    for (const r of filledRows) {
      const qty = effectiveQty(r);
      if (qty <= 0) {
        toast.error(`Row "${r.description}": qty must be greater than 0`);
        return false;
      }
    }
    return true;
  }

  // ── Build request body ──
  function buildBody(action: 'draft' | 'issue') {
    const body: Record<string, unknown> = {
      customer_id: customerId,
      date,
      notes: notes.trim() || undefined,
      items: rows
        .filter((r) => r.description.trim())
        .map((r, idx) => {
          const qty = effectiveQty(r); // resolved area for Sq.ft, raw count otherwise
          return {
            item_id: r.item_id ?? undefined,
            description: r.description.trim(),
            qty,
            unit_price: Math.round((parseFloat(r.priceStr) || 0) * 100),
            discount_type: r.discount_type === 'none' ? undefined : r.discount_type,
            discount_value:
              r.discount_type === 'pct'
                ? parseFloat(r.discountStr) || 0
                : r.discount_type === 'fixed'
                  ? Math.round((parseFloat(r.discountStr) || 0) * 100)
                  : 0,
            sqft_per_box: r.sqft_per_box ?? undefined,
            sqft_per_piece: r.sqft_per_piece ?? undefined,
            // Legacy sqft field kept for compat — for Sq.ft items, qty IS the area
            sqft: isAreaUnit(r.unit) ? qty : r.sqftStr ? parseFloat(r.sqftStr) : undefined,
            sort_order: idx,
            note: r.note.trim() || undefined,
          };
        }),
      action,
    };

    if (invoiceDiscountType !== 'none') {
      body.invoice_discount_type = invoiceDiscountType;
      body.invoice_discount_value =
        invoiceDiscountType === 'pct'
          ? parseFloat(invoiceDiscountStr) || 0
          : Math.round((parseFloat(invoiceDiscountStr) || 0) * 100);
    }

    if (action === 'issue' && collectPayment && parseFloat(paymentAmountStr) > 0) {
      body.payment_amount = Math.round((parseFloat(paymentAmountStr) || 0) * 100);
      body.payment_date = paymentDate;
    }

    // Pickup: default = customer collects everything now; otherwise schedule it (optional date).
    if (action === 'issue') {
      body.picked_up_now = pickedUpNow;
      if (!pickedUpNow && expectedPickupDate) body.expected_pickup_date = expectedPickupDate;
    }

    return body;
  }

  // ── Submit ──
  async function handleSubmit(action: 'draft' | 'issue') {
    if (!validate()) return;

    setSubmitting(true);
    setPendingAction(action);

    try {
      const body = buildBody(action);

      let estimateId: number;
      let warning: CreateEstimateResponse['data']['warning'];

      if (isEdit) {
        // Edit mode: PATCH is draft-only on the server; issue separately afterwards.
        await apiFetch<CreateEstimateResponse>(`/api/estimates/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ ...body, action: 'draft' }),
        });
        estimateId = Number(id);

        if (action === 'issue') {
          const issueRes = await apiFetch<CreateEstimateResponse>(`/api/estimates/${id}/issue`, {
            method: 'POST',
            body: JSON.stringify({
              payment_amount: body.payment_amount,
              payment_date: body.payment_date,
              picked_up_now: body.picked_up_now,
              expected_pickup_date: body.expected_pickup_date,
            }),
          });
          warning = issueRes.data.warning;
          toast.success('Estimate issued');
        } else {
          toast.success('Estimate updated');
        }
      } else {
        const res = await apiFetch<CreateEstimateResponse>('/api/estimates', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        estimateId = res.data.estimate.id;
        warning = res.data.warning;
        toast.success(action === 'issue' ? 'Estimate issued' : 'Estimate saved as draft');
      }

      if (warning) {
        setCreditWarning(warning);
        toast.warning(
          `Credit limit exceeded by ${formatCents(warning.overage)} — estimate was still saved.`,
        );
      }

      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      if (isEdit && id) queryClient.invalidateQueries({ queryKey: ['estimate', id] });

      navigate(`/estimates/${estimateId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  }

  // ── Loading state for edit mode ──
  if (isEdit && editLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Don't flash the populated form when the edit guard is about to redirect
  // (non-editable status or non-admin) — the guard effect handles the navigation.
  const editStatus = isEdit ? editData?.data.estimate.status : undefined;
  const editHasReturns = isEdit ? (editData?.data.returns?.length ?? 0) > 0 : false;
  if (
    isEdit &&
    editData &&
    !(
      (editStatus === 'draft' || (user?.role === 'admin' && editStatus === 'issued')) &&
      !editHasReturns
    )
  ) {
    return null;
  }
  // Editing an already-issued estimate: it stays issued and just saves the changes
  // (single "Save Changes" action; no re-issue / opening payment / draft downgrade).
  const isIssuedEdit = isEdit && editStatus === 'issued';

  const pageTitle = isEdit
    ? `Edit Estimate ${editData?.data.estimate.number ?? ''}`
    : 'New Estimate';

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      {/* ── Top bar ── */}
      <div className="flex items-center gap-3">
        <Link
          to="/estimates"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Estimates
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">{pageTitle}</h1>
      </div>

      {/* ── Credit warning banner (post-submit) ── */}
      {creditWarning && <CreditWarningBanner warning={creditWarning} customerName={customerName} />}

      {/* ── Main form grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left / center: main form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Card: Customer & Date */}
          <div className="rounded-lg border bg-card p-4 space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Customer & Date
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>
                  Customer <span className="text-destructive">*</span>
                </Label>
                <CustomerCombobox
                  value={customerId}
                  displayName={customerName}
                  onChange={(cid, cname) => {
                    setCustomerId(cid || null);
                    setCustomerName(cname);
                  }}
                  disabled={submitting}
                  onConfirmAdvance={() => {
                    // After Tab/Enter selects the customer, jump to the first line's item.
                    const firstKey = rows[0]?.key;
                    if (firstKey) itemInputRegistry.current.get(firstKey)?.focus();
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est-date">Date</Label>
                <Input
                  id="est-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          {/* Card: Line Items */}
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Line Items
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground w-7">
                      #
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">
                      Item / Description
                    </th>
                    <th className="px-1 py-2 w-16 text-xs font-medium text-muted-foreground" />
                    <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground w-20">
                      Qty
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground w-28">
                      Unit Price
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground w-40">
                      Discount
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground w-28">
                      Total
                    </th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <LineItemRow
                      key={row.key}
                      row={row}
                      index={i}
                      onChange={handleRowChange}
                      onRemove={handleRowRemove}
                      lineTotal={lineTotals[i]}
                      disabled={submitting}
                      isLast={i === rows.length - 1}
                      onAddRowAndFocus={addRow}
                      onFocusNextItemSearch={() => {
                        const nextKey = rows[i + 1]?.key;
                        if (nextKey) {
                          itemInputRegistry.current.get(nextKey)?.focus();
                        }
                      }}
                      registerItemInput={makeRegisterInput(row.key)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 border-t bg-muted/10">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                disabled={submitting}
                className="text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Line Item
              </Button>
            </div>
          </div>

          {/* Card: Notes */}
          <div className="rounded-lg border bg-card p-4 space-y-2">
            <Label
              htmlFor="est-notes"
              className="text-sm font-semibold text-muted-foreground uppercase tracking-wide"
            >
              Notes
            </Label>
            <textarea
              id="est-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={submitting}
              placeholder="Optional notes for the customer…"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>
        </div>

        {/* Right: Totals + Actions */}
        <div className="space-y-4">
          {/* Totals panel */}
          <TotalsPanel
            subtotal={totals.subtotal}
            lineDiscountTotal={totals.line_discount_total}
            invoiceDiscountAmount={totals.invoice_discount_amount}
            total={totals.total}
            invoiceDiscountType={invoiceDiscountType}
            invoiceDiscountStr={invoiceDiscountStr}
            onDiscountTypeChange={setInvoiceDiscountType}
            onDiscountStrChange={setInvoiceDiscountStr}
            disabled={submitting}
          />

          {/* Payment card — hidden when editing an already-issued estimate (it stays
              issued; no new opening payment). Otherwise the amount applies on Issue. */}
          {!isIssuedEdit && (
            <IssuePanel
              collectPayment={collectPayment}
              onCollectPaymentChange={setCollectPayment}
              paymentAmountStr={paymentAmountStr}
              onPaymentAmountChange={setPaymentAmountStr}
              paymentDate={paymentDate}
              onPaymentDateChange={setPaymentDate}
              pickedUpNow={pickedUpNow}
              onPickedUpNowChange={setPickedUpNow}
              expectedPickupDate={expectedPickupDate}
              onExpectedPickupDateChange={setExpectedPickupDate}
              total={totals.total}
              disabled={submitting}
            />
          )}

          {/* Actions */}
          <div className="space-y-2">
            <Button
              variant={isIssuedEdit ? 'default' : 'outline'}
              className="w-full"
              onClick={() => handleSubmit('draft')}
              disabled={submitting || collectPayment}
            >
              {submitting && pendingAction === 'draft' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Save as Draft'
              )}
            </Button>
            {!isIssuedEdit && (
              <>
                {collectPayment && (
                  <p className="-mt-1 text-center text-[11px] text-muted-foreground">
                    Uncheck “Collect payment now” to save as a draft.
                  </p>
                )}
                <Button
                  className="w-full"
                  onClick={() => handleSubmit('issue')}
                  disabled={submitting || (collectPayment && !(parseFloat(paymentAmountStr) > 0))}
                >
                  {submitting && pendingAction === 'issue' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Issuing…
                    </>
                  ) : collectPayment ? (
                    `Issue & Collect $${(parseFloat(paymentAmountStr) || 0).toFixed(2)}`
                  ) : (
                    'Issue Estimate'
                  )}
                </Button>
              </>
            )}

            <Link to="/estimates" className="block">
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                disabled={submitting}
              >
                Cancel
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
